import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { CalculatorDisclaimer } from "@/components/brand/CalculatorDisclaimer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  DIMENSIONS,
  DIMENSION_META,
  QUESTIONS,
  type Dimension,
  type Responses,
} from "@/lib/legacyScore";
import { submitLegacyScore } from "@/lib/legacyScore.functions";

export const Route = createFileRoute("/tools/legacy-score/assess")({
  head: () => ({
    meta: [
      { title: "Legacy Score Assessment — Currency Thoughts" },
      {
        name: "description",
        content:
          "Answer 30 short questions across six dimensions to receive your Legacy Score and a personalized roadmap.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LegacyScoreAssessPage,
});

function makeSessionId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function useSessionId(): string {
  const [id] = useState<string>(() => {
    if (typeof window === "undefined") return makeSessionId();
    const KEY = "ct.legacy.session_id";
    const existing = window.sessionStorage.getItem(KEY);
    if (existing) return existing;
    const fresh = makeSessionId();
    window.sessionStorage.setItem(KEY, fresh);
    return fresh;
  });
  return id;
}

const STEPS: { id: Dimension | "email"; label: string }[] = [
  ...DIMENSIONS.map((d) => ({ id: d, label: DIMENSION_META[d].label })),
  { id: "email", label: "Your Results" },
];

function LegacyScoreAssessPage() {
  const navigate = useNavigate();
  const sessionId = useSessionId();
  const submit = useServerFn(submitLegacyScore);

  const [stepIdx, setStepIdx] = useState(0);
  const [responses, setResponses] = useState<Responses>({});
  const [email, setEmail] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const step = STEPS[stepIdx];
  const isEmail = step.id === "email";
  const dim = isEmail ? null : (step.id as Dimension);
  const questions = dim ? QUESTIONS[dim] : [];

  const dimensionComplete = useMemo(() => {
    if (!dim) return true;
    return questions.every((q) => Boolean(responses[`${dim}.${q.id}`]));
  }, [dim, questions, responses]);

  function setAnswer(key: string, value: string) {
    setResponses((prev) => ({ ...prev, [key]: value }));
  }

  function goNext() {
    if (!isEmail && !dimensionComplete) {
      toast.error("Please answer every question on this page.");
      return;
    }
    setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function goBack() {
    setStepIdx((i) => Math.max(0, i - 1));
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSubmit() {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submit({
        data: {
          session_id: sessionId,
          email: email.trim(),
          marketing_opt_in: marketingOptIn,
          responses,
        },
      });
      if (!res.ok) {
        toast.error(res.error || "We couldn't save your score. Please try again.");
        return;
      }
      navigate({
        to: "/tools/legacy-score/result/$token",
        params: { token: res.token },
      });
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const totalAnswered = Object.keys(responses).length;
  const totalQuestions = DIMENSIONS.reduce(
    (acc, d) => acc + QUESTIONS[d].length,
    0,
  );

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />

      <section className="bg-navy-900 text-cream-50 py-12">
        <div className="mx-auto max-w-5xl px-6">
          <Eyebrow color="cream">Legacy Score Assessment</Eyebrow>
          <h1 className="mt-4 font-display font-bold text-cream-50 text-[32px] md:text-[40px] leading-tight">
            Your family's readiness, in 10 minutes.
          </h1>

          {/* Progress dots labeled by dimension */}
          <div className="mt-10 flex flex-wrap items-center gap-3">
            {STEPS.map((s, i) => {
              const active = i === stepIdx;
              const done = i < stepIdx;
              return (
                <div key={s.id} className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "h-2 w-2 rounded-full transition-colors",
                        active && "bg-bronze-400 ring-4 ring-bronze-400/20",
                        done && "bg-bronze-600",
                        !active && !done && "bg-cream-50/30",
                      )}
                      aria-hidden
                    />
                    <span
                      className={cn(
                        "font-sans text-[11px] uppercase tracking-[0.18em]",
                        active ? "text-bronze-400" : done ? "text-cream-50/80" : "text-cream-50/40",
                      )}
                    >
                      {s.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <span className="h-px w-4 bg-cream-50/20" aria-hidden />
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-4 font-sans text-[12px] text-cream-50/60">
            {totalAnswered} of {totalQuestions} answered
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-3xl px-6">
          <div className="bg-white border border-bronze-600/30 rounded-[4px] p-8 md:p-12" style={{ boxShadow: "var(--shadow-soft)" }}>
            {dim && (
              <>
                <Eyebrow color="bronze">{`Dimension ${stepIdx + 1} of 6`}</Eyebrow>
                <h2 className="mt-4 font-display font-semibold text-navy-900 text-[28px] md:text-[34px]">
                  {DIMENSION_META[dim].label}
                </h2>
                <p className="mt-2 font-sans text-[15px] text-charcoal-500">
                  {DIMENSION_META[dim].tagline}
                </p>

                <div className="mt-10 space-y-10">
                  {questions.map((q, qi) => {
                    const key = `${dim}.${q.id}`;
                    const current = responses[key];
                    return (
                      <fieldset key={q.id}>
                        <legend className="font-display font-semibold text-navy-900 text-[18px] leading-snug">
                          <span className="text-bronze-600">{qi + 1}.</span>{" "}
                          {q.prompt}
                        </legend>
                        {q.help && (
                          <p className="mt-2 font-sans text-[13px] text-charcoal-500 italic">
                            {q.help}
                          </p>
                        )}
                        <div className="mt-4 grid gap-2">
                          {q.choices.map((c) => {
                            const checked = current === c.value;
                            return (
                              <label
                                key={c.value}
                                className={cn(
                                  "flex cursor-pointer items-center gap-3 rounded-[4px] border px-4 py-3 transition-colors",
                                  checked
                                    ? "border-bronze-600 bg-cream-50"
                                    : "border-border bg-white hover:border-bronze-600/60",
                                )}
                              >
                                <input
                                  type="radio"
                                  name={key}
                                  value={c.value}
                                  checked={checked}
                                  onChange={() => setAnswer(key, c.value)}
                                  className="h-4 w-4 accent-[color:var(--color-bronze-600)]"
                                />
                                <span className="font-sans text-[15px] text-charcoal-900">
                                  {c.label}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </fieldset>
                    );
                  })}
                </div>
              </>
            )}

            {isEmail && (
              <>
                <Eyebrow color="bronze">Final step</Eyebrow>
                <h2 className="mt-4 font-display font-semibold text-navy-900 text-[28px] md:text-[34px]">
                  Where should we send your Legacy Score?
                </h2>
                <p className="mt-3 font-sans text-[15px] text-charcoal-500 leading-[1.7]">
                  We'll generate your full report on the next screen. You'll
                  also receive a copy by email so you can revisit or share it.
                </p>
                <div className="mt-8 space-y-5">
                  <div>
                    <Label htmlFor="email" className="font-sans text-[13px] uppercase tracking-[0.18em] text-navy-900">
                      Email address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="mt-2 h-12 text-[15px]"
                    />
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={marketingOptIn}
                      onCheckedChange={(v) => setMarketingOptIn(Boolean(v))}
                      className="mt-1"
                    />
                    <span className="font-sans text-[13px] text-charcoal-500 leading-[1.6]">
                      Send me occasional planning insights from Currency
                      Thoughts. No sales pitches. Unsubscribe anytime.
                    </span>
                  </label>
                </div>
                <CalculatorDisclaimer />
              </>
            )}

            <div className="mt-12 flex items-center justify-between gap-4 border-t border-border pt-6">
              <button
                type="button"
                onClick={goBack}
                disabled={stepIdx === 0 || submitting}
                className="font-sans text-[13px] uppercase tracking-[0.18em] text-charcoal-500 disabled:opacity-40 hover:text-navy-900 transition-colors"
              >
                ← Back
              </button>
              {isEmail ? (
                <PrimaryButton onClick={handleSubmit} disabled={submitting} size="lg">
                  {submitting ? "Calculating…" : "See my Legacy Score →"}
                </PrimaryButton>
              ) : (
                <PrimaryButton onClick={goNext} disabled={!dimensionComplete}>
                  Continue →
                </PrimaryButton>
              )}
            </div>
          </div>

          <p className="mt-6 text-center font-sans text-[12px] text-charcoal-500">
            Your responses are private.{" "}
            <Link to="/tools/legacy-score" className="underline hover:text-navy-900">
              About the methodology
            </Link>
            .
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}